import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import LessonChatWidget from "../components/LessonChatWidget";
import ModuleCompletionModal from "../components/ModuleCompletionModal";
import { useTranslation } from "../hooks/useTranslation";

import {
  FileText,
  ArrowRight,
  ArrowLeft,
  GraduationCap,
  ChevronLeft,
} from "lucide-react";
import {
  getModule,
  getMe,
  getLessonProgress,
  upsertLessonProgress,
} from "../api/client";

const GLOBAL_NAV_WIDTH = 240;

const brandBlue = "#1E88E5";

export default function ModulePage() {
  const { t } = useTranslation();
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();

  const [moduleData, setModuleData] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [user, setUser] = useState(null);
  const [lessonProgress, setLessonProgress] = useState([]);

  const effectiveModuleId = moduleId || "M1_WR_1K";
  const isStandaloneLesson = Boolean(lessonId);

  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Modul + Modulliste laden
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getModule(effectiveModuleId);
        if (!data || !Array.isArray(data.lessons)) {
          throw new Error("invalid_module_structure");
        }
        setModuleData(data);

        const initialLessonId = lessonId || data.lessons[0]?.id || null;
        setActiveLessonId(initialLessonId);
      } catch (e) {
        console.error("ModulePage load error:", e);
        setError(t.modules.moduleLoadError);
      } finally {
        setLoading(false);
      }
    })();
  }, [effectiveModuleId, lessonId, t]);

  // User + Lesson-Progress laden
  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        if (!me || !me.id) return;
        setUser(me);

        const lp = await getLessonProgress(me.id);
        setLessonProgress(Array.isArray(lp) ? lp : []);
      } catch (e) {
        console.error("ModulePage user/progress load error:", e);
      }
    })();
  }, []);

  const handleBackToModule = () => {
    navigate(`/Lessons?module=${effectiveModuleId}`);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-2 text-muted">{t.modules.moduleLoading}</div>
      </Container>
    );
  }

  if (error || !moduleData) {
    return (
      <Container className="py-5 text-center">
        <p className="text-danger mb-3">
          {error || t.modules.unknownError}
        </p>
        <p className="text-muted mb-3">
          {t.modules.tryAgainLater}
        </p>
        <Link to="/Lessons">
          <Button variant="outline-primary">{t.modules.backToLessons}</Button>
        </Link>
      </Container>
    );
  }

  const activeLesson =
    moduleData.lessons.find((l) => l.id === activeLessonId) ||
    moduleData.lessons[0];

  const lessonIndex =
    moduleData.lessons.findIndex((l) => l.id === activeLesson?.id) ?? 0;

  // Progress-Record für aktuelle Lesson
  const currentLessonProgress =
    user && activeLesson
      ? lessonProgress.find(
          (p) => p.lesson_id === activeLesson.id && p.user_id === user.id
        ) || null
      : null;

  const isLessonCompleted = currentLessonProgress?.status === "completed";

  const goPrevLesson = () => {
    if (lessonIndex > 0) {
      setActiveLessonId(moduleData.lessons[lessonIndex - 1].id);
    }
  };

  // Lektion als fertig markieren + zur nächsten springen
  const handleCompleteAndNext = async () => {
    if (!user || !activeLesson) return;

    const now = new Date();
    const completedDate = now.toISOString().slice(0, 19).replace("T", " ");

    try {
      await upsertLessonProgress({
        id: currentLessonProgress?.id || undefined,
        lesson_id: activeLesson.id,
        user_id: user.id,
        status: "completed",
        progress_percent: 100,
        time_spent_minutes: currentLessonProgress?.time_spent_minutes || 0,
        completed_date: completedDate,
      });

      setLessonProgress((prev) => {
        const others = prev.filter(
          (p) => !(p.lesson_id === activeLesson.id && p.user_id === user.id)
        );
        return [
          ...others,
          {
            ...(currentLessonProgress || {}),
            id: currentLessonProgress?.id || `local_${Date.now()}`,
            lesson_id: activeLesson.id,
            user_id: user.id,
            status: "completed",
            progress_percent: 100,
            time_spent_minutes: currentLessonProgress?.time_spent_minutes || 0,
            completed_date: completedDate,
          },
        ];
      });

      if (lessonIndex === moduleData.lessons.length - 1) {
        // LETZTE LEKTION - Modal öffnen
        console.log("Opening completion modal!");
        setShowCompletionModal(true);
      } else {
        // Zur nächsten Lektion springen
        setActiveLessonId(moduleData.lessons[lessonIndex + 1].id);
      }
    } catch (e) {
      console.error("ModulePage upsertLessonProgress error:", e);
    }
  };

  // Helper: Schwierigkeitsgrad Label
  const getDifficultyLabel = (difficulty) => {
    const labels = {
      de: { beginner: 'Anfänger', intermediate: 'Fortgeschritten', advanced: 'Experte' },
      en: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
    };
    return labels[t._lang]?.[difficulty] || difficulty;
  };

  const getDifficultyVariant = (difficulty) => {
    const variants = { beginner: 'success', intermediate: 'warning', advanced: 'danger' };
    return variants[difficulty] || 'secondary';
  };

  // Helper: Kategorie Label
  const getCategoryLabel = (category) => {
    const labels = {
      de: {
        grundlagen: 'Grundlagen',
        bilanzierung: 'Bilanzierung',
        kostenrechnung: 'Kostenrechnung',
        controlling: 'Controlling',
        steuern: 'Steuern',
      },
      en: {
        grundlagen: 'Fundamentals',
        bilanzierung: 'Accounting',
        kostenrechnung: 'Cost Accounting',
        controlling: 'Controlling',
        steuern: 'Taxes',
      },
    };
    return labels[t._lang]?.[category] || category;
  };

  return (
    <>
      {/* Main Content */}
      <main
        className="modulepage-main"
        style={{
          marginLeft: 0,
          background: "var(--bg-secondary)",
          minHeight: "100vh",
          transition: "margin-left 280ms ease-in-out",
        }}
      >
        <Container
          fluid
          className="py-2 px-3"
        >
          {/* Zurück-Button (nur in Standalone-Ansicht) */}
          {isStandaloneLesson && (
            <div className="mb-1">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleBackToModule}
                className="d-flex align-items-center gap-1"
              >
                <ChevronLeft size={16} />
                {t.modules.backToModule}
              </Button>
            </div>
          )}

          {/* Kopfbereich */}
          <div className="mb-2">
            <div className="d-flex align-items-center gap-2 mb-0">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: "rgba(30,136,229,0.1)",
                }}
              >
                <GraduationCap size={20} style={{ color: brandBlue }} />
              </div>
              <div>
                <h4 className="mb-0">{moduleData.title}</h4>
                <div className="d-flex flex-wrap gap-1">
                  {moduleData.year_level && (
                    <Badge bg="primary" style={{ fontSize: '0.7rem' }}>
                      {moduleData.year_level}. {t.modules.class} HAK
                    </Badge>
                  )}
                  {moduleData.semester && (
                    <Badge bg="info" style={{ fontSize: '0.7rem' }}>{t.modules.semester} {moduleData.semester}</Badge>
                  )}
                </div>
              </div>
            </div>
            {moduleData.description && !isStandaloneLesson && (
              <p className="text-muted mb-0 small">{moduleData.description}</p>
            )}
          </div>

          {/* Content */}
          <Row className="g-2">
            {/* Lesson Content */}
            <Col lg={12}>
              <Card className="border-0 shadow-sm mb-2">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <h5 className="mb-0">{activeLesson.title}</h5>

                        {activeLesson.category && (
                          <Badge bg="light" text="dark">
                            {getCategoryLabel(activeLesson.category)}
                          </Badge>
                        )}

                        {activeLesson.difficulty && (
                          <Badge bg={getDifficultyVariant(activeLesson.difficulty)}>
                            {getDifficultyLabel(activeLesson.difficulty)}
                          </Badge>
                        )}
                      </div>

                      {activeLesson.learning_objectives &&
                        activeLesson.learning_objectives.length > 0 && (
                          <div className="text-muted small">
                            {t.lessons.learningGoals}
                            <ul className="mb-0 ps-3">
                              {activeLesson.learning_objectives.map((obj) => (
                                <li key={obj}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>

                    {!isStandaloneLesson && (
                      <div className="text-end">
                        <small className="text-muted d-block mb-1">
                          {t.modules.lessonOf
                            .replace('{{current}}', lessonIndex + 1)
                            .replace('{{total}}', moduleData.lessons.length)}
                        </small>
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={goPrevLesson}
                            disabled={lessonIndex === 0}
                          >
                            <ArrowLeft size={16} className="me-1" />
                            {t.modules.back}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Theorie */}
                  {Array.isArray(activeLesson.content_sections) &&
                    activeLesson.content_sections.length > 0 && (
                      <div className="mb-1">
                        <div className="d-flex align-items-center mb-1">
                          <FileText
                            size={18}
                            className="me-2 text-secondary"
                          />
                          <h5 className="mb-0">{t.lessons.theory}</h5>
                        </div>
                        {activeLesson.content_sections.map((section) => (
                          <div key={section.id} className="mb-2">
                            {section.title && (
                              <h6 className="fw-semibold mb-1">
                                {section.title}
                              </h6>
                            )}
                            <div className="rounded p-2" style={{ backgroundColor: "var(--bg-secondary)" }}>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm, remarkMath]}
                                rehypePlugins={[rehypeKatex]}
                                components={{
                                  h1: (props) => <h5 className="mt-2 mb-2" {...props} />,
                                  h2: (props) => <h6 className="mt-2 mb-2" {...props} />,
                                  ul: (props) => (
                                    <ul
                                      className="mb-2"
                                      style={{ paddingLeft: "1.2rem" }}
                                      {...props}
                                    />
                                  ),
                                  table: ({ node, ...props }) => (
                                    <div className="lesson-table-wrapper table-responsive">
                                      <table className="table table-sm mb-0" {...props} />
                                    </div>
                                  ),
                                }}
                              >
                                {section.markdown}
                              </ReactMarkdown>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Aktionen unten: Nächste Lektion */}
                  <div className="d-flex justify-content-end mt-2">
                    <Button
                      variant={isLessonCompleted ? "success" : "primary"}
                      size="sm"
                      onClick={handleCompleteAndNext}
                    >
                      {lessonIndex === moduleData.lessons.length - 1
                        ? isLessonCompleted
                          ? t.modules.completeLesson
                          : t.modules.finishModule
                        : isLessonCompleted
                        ? t.modules.completeAndNext
                        : t.modules.completeAndNext}
                      <ArrowRight size={16} className="ms-1" />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>

      {/* AI Chat Widget */}
      <LessonChatWidget
        lessonContext={{
          lessonId: activeLesson?.id,
          title: activeLesson?.title,
          description: activeLesson?.description,
          moduleTitle: moduleData?.title,
          content:
            activeLesson?.content_sections
              ?.map((s) => s.markdown)
              .join("\n\n") || "",
        }}
      />

      <ModuleCompletionModal
        show={showCompletionModal}
        onHide={() => setShowCompletionModal(false)}
        moduleData={moduleData}
        completedLessonsCount={moduleData?.lessons?.length || 0}
        userId={user?.id}
      />
    </>
  );
}
